import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, ShieldCheck, LogOut, Ticket, X,
  PlayCircle, GraduationCap, LineChart, Layers, Users,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import { paymentConfig, createOrder, verifyPayment, paymentStatus, previewCoupon } from "../lib/api";
import { loadRazorpay } from "../lib/razorpay";
import { clearAuth, getUser, setUser } from "../lib/auth";

const BENEFITS = [
  { icon: PlayCircle, title: "Short video access", desc: "Bite-sized daily market clips" },
  { icon: GraduationCap, title: "Education videos", desc: "Full learning library, from basics to pro" },
  { icon: LineChart, title: "Crypto predictions", desc: "Weekly predictions on top coins" },
  { icon: Layers, title: "Other courses", desc: "Access to additional courses inside the app" },
  { icon: Users, title: "Community access", desc: "Private community with mentors & peers" },
];

export default function Payment() {
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // {code, final_inr, discount_inr}
  const [couponLoading, setCouponLoading] = useState(false);
  const user = getUser();

  useEffect(() => {
    (async () => {
      try {
        const [cfg, status] = await Promise.all([paymentConfig(), paymentStatus()]);
        setConfig(cfg);
        if (status.paid) {
          nav("/success", { replace: true });
          return;
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          clearAuth();
          nav("/login", { replace: true });
          return;
        }
      } finally {
        setChecking(false);
      }
    })();
  }, [nav]);

  const handlePay = async () => {
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Failed to load payment gateway. Check your internet connection.");
        setLoading(false);
        return;
      }

      const order = await createOrder({ coupon_code: couponApplied?.code || undefined });

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "GGES Ventures",
        description: "Pre-join lifetime access",
        order_id: order.order_id,
        prefill: {
          name: order.prefill?.name || "",
          contact: order.prefill?.contact || "",
        },
        notes: {
          user_id: (getUser() || {}).id || "",
        },
        theme: { color: "#FF5A00" },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.message("Payment cancelled");
          },
        },
        handler: async (response) => {
          try {
            const verified = await verifyPayment({
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            const u = getUser() || {};
            setUser({ ...u, paid: true, access_unlock_iso: verified.access_unlock_iso });
            toast.success("Payment successful");
            nav("/success", { replace: true });
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed");
          } finally {
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed to start");
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    nav("/login", { replace: true });
  };

  if (checking || !config) {
    return (
      <AppShell>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <Loader2 className="w-6 h-6 animate-spin text-brand" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="min-h-screen bg-white flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-5">
          <Logo size="sm" />
          <button
            onClick={handleLogout}
            data-testid="payment-logout-btn"
            className="h-9 px-3 rounded-full border border-neutral-200 flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>

        <div className="screen screen--with-cta flex-1">
          {/* Header */}
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#FFF0E6] text-brand text-[10px] font-semibold tracking-widest uppercase">
              <span className="w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
              Pre-join offer live
            </div>
            <h1
              className="mt-4 font-display font-bold text-[26px] tracking-tight"
              data-testid="payment-title"
            >
              Complete your joining
            </h1>
            <p className="mt-1.5 text-[13px] text-neutral-500 max-w-xs mx-auto">
              {user?.name ? `${user.name.split(" ")[0]}, o` : "O"}ne small step to lock in your seat.
            </p>
          </div>

          {/* Pricing card */}
          <div className="mt-8 relative rounded-3xl border-2 border-brand/40 bg-gradient-to-br from-white via-[#FFF6EE] to-[#FFD9B8] p-6 overflow-hidden">
            <div className="absolute -right-12 top-4 rotate-45 bg-brand text-white text-[11px] font-black tracking-widest px-14 py-1 shadow-lg">
              SAVE 51%
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand font-bold">
              Joining fee
            </div>

            <div className="mt-3 flex items-center gap-2">
              <span
                className="text-[16px] text-neutral-500 line-through decoration-[2.5px] decoration-red-500/70 font-semibold tabular-nums"
                data-testid="original-price"
              >
                ${config.usd_original ?? 250}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-red-500/10 text-red-600 text-[10px] font-bold tracking-widest">
                -{config.discount_percent}%
              </span>
            </div>

            <div className="mt-1 flex items-baseline gap-2">
              <span
                className="font-display font-black text-[54px] tracking-tighter tabular-nums leading-none text-brand-gradient"
                data-testid="final-price"
              >
                ${Number(config.usd_final ?? 122.5).toFixed(2)}
              </span>
              <span className="text-[13px] font-semibold text-emerald-600">
                You save ${(Number(config.usd_original ?? 250) - Number(config.usd_final ?? 122.5)).toFixed(2)}
              </span>
            </div>

            <div className="mt-2 text-[13px] text-neutral-700 tabular-nums">
              Charged in INR ≈{" "}
              <span className="font-semibold text-black">₹{config.final_amount.toLocaleString("en-IN")}</span>
              <span className="text-neutral-400 line-through ml-2">
                ₹{config.original_amount.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="mt-4 flex items-center gap-2 text-[11.5px] text-neutral-600">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              One-time · Lifetime access · Secure Razorpay checkout
            </div>

            <div
              className="hidden"
              data-testid="discount-badge"
              data-discount={config.discount_percent}
            />
          </div>

          {/* Coupon apply */}
          <div className="mt-4 p-4 rounded-2xl border border-dashed border-neutral-300 bg-white" data-testid="coupon-block">
            {couponApplied ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Ticket className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-black">Coupon <span className="font-mono">{couponApplied.code}</span> applied</div>
                    <div className="text-[11.5px] text-emerald-600">You save ₹{couponApplied.discount_inr.toLocaleString("en-IN")} · Pay ₹{couponApplied.final_inr.toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <button
                  onClick={() => { setCouponApplied(null); setCoupon(""); }}
                  data-testid="coupon-remove-btn"
                  className="w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center"
                  aria-label="Remove coupon"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Ticket className="w-4 h-4 text-brand" />
                  <span className="text-[12px] font-semibold text-neutral-700">Have a coupon?</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="coupon-input"
                    className="input-native flex-1 uppercase font-mono"
                    placeholder="Enter code"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    maxLength={30}
                  />
                  <button
                    onClick={async () => {
                      if (!coupon.trim()) return;
                      setCouponLoading(true);
                      try {
                        const r = await previewCoupon(coupon.trim());
                        setCouponApplied({ code: r.coupon.code, final_inr: r.final_inr, discount_inr: r.discount_inr });
                        toast.success(`Coupon applied · Save ₹${r.discount_inr.toLocaleString("en-IN")}`);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Invalid coupon");
                      } finally {
                        setCouponLoading(false);
                      }
                    }}
                    disabled={!coupon.trim() || couponLoading}
                    data-testid="coupon-apply-btn"
                    className="h-11 px-4 rounded-full bg-black text-white text-[13px] font-semibold disabled:opacity-50"
                  >
                    {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Benefits */}
          <div className="mt-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
              What's included
            </div>
            <ul className="mt-4 space-y-3.5" data-testid="benefits-list">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <li
                    key={i}
                    className="flex items-start gap-3.5"
                    data-testid={`benefit-item-${i}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#FFF0E6] text-brand flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-[14px] text-black">{b.title}</div>
                      <div className="text-[12.5px] text-neutral-500 mt-0.5">{b.desc}</div>
                    </div>
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 flex-shrink-0 mt-1" />
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Additional info */}
          <div className="mt-6 p-4 rounded-2xl bg-neutral-50 border border-neutral-200">
            <p className="text-[12.5px] text-neutral-700 leading-relaxed">
              <span className="font-semibold text-black">Access unlocks 10 August 2026.</span>{" "}
              More courses and material will be added regularly — accessible right inside the app once launch day arrives.
            </p>
          </div>
        </div>

        {/* Sticky bottom CTA */}
        <div className="sticky-cta">
          <button
            onClick={handlePay}
            disabled={loading}
            data-testid="pay-now-btn"
            className="btn-brand btn-brand--pulse w-full h-11 flex items-center justify-center gap-2 text-[14px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Processing…
              </>
            ) : (
              <>Pay ₹{(couponApplied?.final_inr ?? config.final_amount).toLocaleString("en-IN")}</>
            )}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
